import { ref, computed, inject, type Ref, onMounted, onUnmounted, watch } from 'vue'

import type { ValidationRuleObject } from '@staff/shared'

import {
  REGISTER_VALIDATION_HANDLER,
  UNREGISTER_VALIDATION_HANDLER,
  type ValidationHandler,
} from '../components/AppValidationGroup/AppValidationGroup.types'

export type ValidationProps = {
  rules?: (ValidationRuleObject | null)[]
  validationOptions?: {
    validateOnModelChange?: boolean
  }
}

export const validateInput = async (value: unknown, ruleObjects: ValidationRuleObject[]) => {
  return await Promise.all(
    ruleObjects.map(async (ruleObject) => {
      return await ruleObject?.rule?.(value)
    }),
  )
}

const notNull = <T>(value: T | null): value is T => value !== null

export const useInputValidation = (
  value: () => unknown,
  rules: () => (ValidationRuleObject | null)[] | undefined,
  inputEl: Ref<HTMLElement | null | undefined>,
) => {
  const registerValidationHandler = inject(REGISTER_VALIDATION_HANDLER, undefined)
  const unregisterValidationHandler = inject(UNREGISTER_VALIDATION_HANDLER, undefined)

  const id = Symbol('InputValidation')

  const errorMessages = ref<string[]>([])
  const invalid = computed(() => errorMessages.value.length > 0)

  const validate: ValidationHandler = async (options?: { focusOnError: Ref<boolean> }) => {
    try {
      const result = await validateInput(value(), rules()?.filter(notNull) ?? [])

      errorMessages.value = result.filter((r) => typeof r === 'string') as string[]

      if (invalid.value && options?.focusOnError.value) {
        inputEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        options.focusOnError.value = false
      }

      return result
    } catch (err) {
      console.error('Failed in validate method', err)
      errorMessages.value = [(err as Error).message]
      return [(err as Error).message]
    }
  }

  onMounted(() => {
    if (rules() && registerValidationHandler) {
      registerValidationHandler({ id, errorMessages: computed(() => errorMessages.value), validate })
    }
  })

  onUnmounted(() => {
    if (rules() && unregisterValidationHandler) {
      unregisterValidationHandler(id)
    }
  })

  watch(
    () => rules(),
    async (newValue, oldValue) => {
      if (
        newValue?.length !== oldValue?.length ||
        newValue?.some((rule, index) => rule?.id !== oldValue?.[index]?.id)
      ) {
        await validate()
      }
    },
  )

  return {
    invalid,
    errorMessages: computed(() => errorMessages.value),
    validate,
  }
}
